'use client';

/** @jsxImportSource @emotion/react */
import {
  Badge,
  Button,
  Card,
  compactObject,
  Container,
  FlexItem,
  FlexLayout,
  Icon,
  Image,
  responsive,
  Typography,
} from '@wci-tech/core-ui';
import { css } from '@emotion/react';
import { useTranslations } from 'next-intl';
import { BsArrowUpRight } from 'react-icons/bs';

import { useThemeMode } from '@/context';
import { useScrollToTarget } from '@/hooks/useScrollToTarget';

import { useStyles } from './WebPlatform.styles';

export const createResponsiveImage = (index: number, isDark: boolean) => {
  const themeSuffix = isDark ? '' : 'Light';

  return {
    src: `/images/webPlatform${index}Mobile${themeSuffix}.png`,
    sources: [
      {
        srcSet: `/images/webPlatform${index}Desktop${themeSuffix}.png`,
        media: '(min-width: 1024px)',
        type: 'image/png',
      },
      {
        srcSet: `/images/webPlatform${index}Tablet${themeSuffix}.png`,
        media: '(min-width: 768px)',
        type: 'image/png',
      },
    ],
  };
};

const WebPlatform = () => {
  const t = useTranslations('WebPlatform');
  const { isDark } = useThemeMode();
  const styles = useStyles();

  const scrollToContact = useScrollToTarget({
    targetId: 'contact',
    offset: 82,
  });

  const platformCards = [
    {
      id: 'dsaas',
      imageIndex: 1,
      title: t('cards.designsystem.title'),
      description: t('cards.designsystem.description'),
      highlight: t('cards.designsystem.highlight'),
      buttonText: t('cards.designsystem.buttonText'),
      showButton: true,
      chip: {
        label: t('cards.designsystem.badge'),
        bg: isDark ? '#002F2E' : '#B7F0EF',
        color: isDark ? '#4EE1B5' : '#0A5755',
      },
    },
    {
      id: 'dataconsumptionengine',
      imageIndex: 2,
      title: t('cards.dataconsumptionengine.title'),
      description: t('cards.dataconsumptionengine.description'),
      highlight: t('cards.dataconsumptionengine.highlight'),
      buttonText: t('cards.dataconsumptionengine.buttonText'),
      showButton: true,
      chip: {
        label: t('cards.dataconsumptionengine.badge'),
        bg: isDark ? '#3E2F00' : '#FFEEB8',
        color: isDark ? '#FFC400' : '#523F00',
      },
    },
    {
      id: 'uianalytics',
      imageIndex: 3,
      title: t('cards.uianalytics.title'),
      description: t('cards.uianalytics.description'),
      highlight: t('cards.uianalytics.highlight'),
      buttonText: t('cards.uianalytics.buttonText'),
      showButton: false,
      chip: {
        label: t('cards.uianalytics.badge'),
        bg: '',
        color: '',
      },
    },
    {
      id: 'webaudits',
      imageIndex: 4,
      title: t('cards.webapplicationaudit.title'),
      description: t('cards.webapplicationaudit.description'),
      highlight: t('cards.webapplicationaudit.highlight'),
      buttonText: t('cards.webapplicationaudit.buttonText'),
      showButton: false,
      chip: {
        label: t('cards.webapplicationaudit.badge'),
        bg: isDark ? '#091C46' : '#B8DBFF',
        color: isDark ? '#C4CAF1' : '#1B2358',
      },
    },
    {
      id: 'deliveryobservability',
      imageIndex: 5,
      title: t('cards.deliveryobservability.title'),
      description: t('cards.deliveryobservability.description'),
      highlight: t('cards.deliveryobservability.highlight'),
      buttonText: t('cards.deliveryobservability.buttonText'),
      showButton: false,
      chip: {
        label: t('cards.deliveryobservability.badge'),
        bg: '',
        color: '',
      },
    },
  ];

  return (
    <section css={styles.wrapper} id="web-platform">
      <Container css={styles.container}>
        <Typography variant="h2" customStyles={styles.sectionTitle}>
          {t('sectionTitle')}
        </Typography>

        {platformCards.map((card, index) => {
          const isContentLeft = index % 2 === 0;
          const image = createResponsiveImage(card.imageIndex, isDark);

          const isDataConsumptionEngine = card.id === 'dataconsumptionengine';

          const buttonProps = compactObject({
            href: isDataConsumptionEngine ? undefined : '/coming-soon',
            onClick: isDataConsumptionEngine ? scrollToContact : undefined,
          });

          return (
            <Card
              key={card.id}
              id={card.id}
              appearance="basic"
              customStyles={{
                container: styles.cardContainer,
              }}
            >
              {/* Mobile only: Badge -> Title */}
              <div css={styles.mobileHeader}>
                {card.chip.label && (
                  <div css={styles.badgeWrapper}>
                    <Badge
                      content={card.chip.label}
                      {...(card.id === 'webaudits'
                        ? {
                            effect: 'lightSweep' as const,
                            lightSweepAnimationPosition: 'left' as const,
                          }
                        : {})}
                      customStyles={{
                        badge: styles.badge(card.chip.bg, card.chip.color),
                      }}
                    />
                  </div>
                )}

                <Typography variant="h3" customStyles={styles.title}>
                  {card.title}
                </Typography>
              </div>

              <FlexLayout
                direction={{
                  default: 'column',
                  md: 'row',
                }}
                wrap="nowrap"
                columns={12}
                alignItems={{
                  default: 'flex-start',
                  md: 'stretch',
                }}
                justifyContent="flex-start"
                fullWidth
                gap={{ default: 20, md: 0 }}
                padding={{ default: 0 }}
              >
                <FlexItem
                  columnSpan={{ default: 12, md: 6 }}
                  basis={{ default: '100%', md: '50%' }}
                  customStyles={{
                    flexItem: styles.contentItem(isContentLeft),
                  }}
                >
                  <div css={styles.contentCol}>
                    {/* Tablet + Desktop: original Badge -> Title */}
                    <div css={styles.desktopHeader}>
                      {card.chip.label && (
                        <div css={styles.badgeWrapper}>
                          <Badge
                            content={card.chip.label}
                            {...(card.id === 'webaudits'
                              ? {
                                  effect: 'lightSweep' as const,
                                  lightSweepAnimationPosition: 'left' as const,
                                }
                              : {})}
                            customStyles={{
                              badge: styles.badge(card.chip.bg, card.chip.color),
                            }}
                          />
                        </div>
                      )}

                      <Typography variant="h3" customStyles={styles.title}>
                        {card.title}
                      </Typography>
                    </div>

                    <Typography variant="p" customStyles={styles.description}>
                      {card.description}
                    </Typography>

                    <Typography variant="h4" customStyles={styles.highlightText}>
                      {card.highlight}
                    </Typography>

                    {card.showButton && (
                      <div css={styles.buttonContainer}>
                        <Button
                          variant={isDark ? 'secondary' : 'primary'}
                          appearance="outline"
                          shape="pill"
                          effect="icon-shift"
                          iconPlacement="right"
                          icon={
                            <Icon
                              name={BsArrowUpRight}
                              size={{
                                default: '14px',
                                lg: '16px',
                              }}
                            />
                          }
                          css={css`
                            ${responsive.create({
                              width: {
                                default: '100%',
                                md: 'auto',
                              },
                            })}
                          `}
                          {...buttonProps}
                          customStyles={{
                            icon: {
                              right: styles.btnIcon,
                            },
                          }}
                        >
                          {card.buttonText}
                        </Button>
                      </div>
                    )}
                  </div>
                </FlexItem>

                <FlexItem
                  columnSpan={{ default: 12, md: 6 }}
                  basis={{ default: '100%', md: '50%' }}
                  customStyles={{
                    flexItem: styles.imageItem(isContentLeft),
                  }}
                >
                  <div css={styles.imageCol}>
                    <Image
                      key={`${card.id}-${isDark ? 'dark' : 'light'}`}
                      src={image.src}
                      sources={image.sources}
                      isImgDecorative={false}
                      altText={card.title}
                      width="100%"
                      aspectRatio="4 / 3"
                      objectFit="contain"
                      customStyles={styles.image}
                    />
                  </div>
                </FlexItem>
              </FlexLayout>
            </Card>
          );
        })}
      </Container>
    </section>
  );
};

export default WebPlatform;
